import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Plus, Save, Wand2, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { ApiError } from '@orisini/ui/lib/api'
import { cn } from '@orisini/ui/lib/utils'
import { accountsApi, type AccountDto } from '@/features/accounts/api'
import { DOW_LABELS, hhmmToMinute, minuteToHHMM, slotsApi, type SlotPreview } from '../api'
import { GenerateError, generateSlots, mergeGenerated, MAX_PER_DAY } from '../generate'

interface DraftSlot {
  dow: number
  minuteOfDay: number
}

const PRESETS: Array<{ label: string; make: () => DraftSlot[] }> = [
  {
    label: '1×/day 09:00',
    make: () => Array.from({ length: 7 }, (_, d) => ({ dow: d, minuteOfDay: 540 })),
  },
  {
    label: '3×/day (09, 13, 20)',
    make: () =>
      Array.from({ length: 7 }, (_, d) => d).flatMap((d) =>
        [540, 780, 1200].map((m) => ({ dow: d, minuteOfDay: m })),
      ),
  },
  {
    label: 'Weekdays 08 & 19',
    make: () => [1, 2, 3, 4, 5].flatMap((d) => [480, 1140].map((m) => ({ dow: d, minuteOfDay: m }))),
  },
]

export default function SlotsPage() {
  const [accounts, setAccounts] = useState<AccountDto[]>([])
  const [accountId, setAccountId] = useState('')
  const [draft, setDraft] = useState<DraftSlot[]>([])
  const [timezone, setTimezone] = useState('Asia/Jakarta')
  const [preview, setPreview] = useState<SlotPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [timeInput, setTimeInput] = useState('09:00')
  const [dayInput, setDayInput] = useState(1)

  // --- pembuat slot otomatis ---
  const [genDays, setGenDays] = useState<number[]>([1, 2, 3, 4, 5])
  const [genStart, setGenStart] = useState('08:00')
  const [genEnd, setGenEnd] = useState('20:00')
  const [genPerDay, setGenPerDay] = useState('3')

  const account = accounts.find((a) => a.id === accountId)

  /**
   * Pratinjau dihitung ulang tiap ketikan supaya masukan mustahil (rentang
   * terbalik, slot terlalu rapat) ketahuan SEBELUM tombol ditekan.
   */
  const { genPreview, genError, genWarning } = useMemo(() => {
    const start = hhmmToMinute(genStart)
    const end = hhmmToMinute(genEnd)
    const perDay = Number(genPerDay)
    if (start === null || end === null) {
      return { genPreview: null, genError: 'Invalid time format (example: 08:00)', genWarning: null }
    }
    if (genDays.length === 0) {
      return { genPreview: null, genError: 'Select at least one day', genWarning: null }
    }
    try {
      const { slots, minGapMinutes } = generateSlots({
        days: genDays,
        startMinute: start,
        endMinute: end,
        perDay,
      })
      const total = mergeGenerated(draft, slots, genDays).length
      // Contoh dari hari pertama saja — tiap hari diacak sendiri, jadi tidak ada
      // satu daftar jam yang mewakili semuanya.
      const contoh = slots
        .filter((s) => s.dow === genDays[0])
        .map((s) => minuteToHHMM(s.minuteOfDay))

      // Pacing akun menahan posting yang terlalu berdekatan — slot yang lebih
      // rapat dari jeda minimum hanya akan tergeser saat tayang.
      const minGap = account?.minGapMinutes ?? 0
      let warning: string | null = null
      if (minGapMinutes !== null && minGap > 0 && minGapMinutes < minGap) {
        warning = `There's a ${minGapMinutes}-minute gap, tighter than the account's minimum gap (${minGap} minutes) — posts will be shifted automatically.`
      }
      return { genPreview: { total, contoh, minGapMinutes }, genError: null, genWarning: warning }
    } catch (err) {
      return {
        genPreview: null,
        genError: err instanceof GenerateError ? err.message : 'Invalid input',
        genWarning: null,
      }
    }
  }, [genDays, genStart, genEnd, genPerDay, draft, account?.minGapMinutes])

  function applyGenerate() {
    const start = hhmmToMinute(genStart)
    const end = hhmmToMinute(genEnd)
    if (start === null || end === null || genError) {
      toast.error(genError ?? 'Invalid time format')
      return
    }
    try {
      const { slots } = generateSlots({
        days: genDays,
        startMinute: start,
        endMinute: end,
        perDay: Number(genPerDay),
      })
      const hasil = mergeGenerated(draft, slots, genDays)
      setDraft(hasil)
      setDirty(true)
      toast.success(`${slots.length} slots created on ${genDays.length} days — remember to Save`)
    } catch (err) {
      toast.error(err instanceof GenerateError ? err.message : 'Couldn\'t create slots')
    }
  }

  const loadAccounts = useCallback(async () => {
    const res = await accountsApi.list()
    setAccounts(res.accounts)
    if (res.accounts.length && !accountId) setAccountId(res.accounts[0].id)
    setLoading(false)
  }, [accountId])

  const loadSlots = useCallback(async (accId: string) => {
    const [res, prev] = await Promise.all([slotsApi.list(accId), slotsApi.preview(accId)])
    setDraft(res.slots.map((s) => ({ dow: s.dow, minuteOfDay: s.minuteOfDay })))
    setTimezone(res.timezone)
    setPreview(prev)
    setDirty(false)
  }, [])

  useEffect(() => {
    loadAccounts().catch((e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : 'Couldn\'t load accounts'),
    )
  }, [loadAccounts])

  useEffect(() => {
    if (accountId) {
      loadSlots(accountId).catch((e: unknown) =>
        toast.error(e instanceof ApiError ? e.message : 'Couldn\'t load slots'),
      )
    }
  }, [accountId, loadSlots])

  const byDow = useMemo(() => {
    const map = new Map<number, number[]>()
    for (const s of draft) {
      const list = map.get(s.dow) ?? []
      list.push(s.minuteOfDay)
      map.set(s.dow, list)
    }
    for (const list of map.values()) list.sort((a, b) => a - b)
    return map
  }, [draft])

  function addSlot(dow: number, minuteOfDay: number) {
    if (draft.some((s) => s.dow === dow && s.minuteOfDay === minuteOfDay)) {
      toast.error('A slot at that day and time already exists')
      return
    }
    setDraft((d) => [...d, { dow, minuteOfDay }])
    setDirty(true)
  }

  function removeSlot(dow: number, minuteOfDay: number) {
    setDraft((d) => d.filter((s) => !(s.dow === dow && s.minuteOfDay === minuteOfDay)))
    setDirty(true)
  }

  async function save() {
    setSaving(true)
    try {
      await slotsApi.bulk(accountId, draft)
      setDirty(false)
      setPreview(await slotsApi.preview(accountId))
      toast.success('Slots saved — the queue schedule is recalculated')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn\'t save slots')
    } finally {
      setSaving(false)
    }
  }

  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>
  if (accounts.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Add a Pinterest account first to set up slots.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Time slots</h1>
          <p className="text-sm text-muted-foreground">
            {draft.length} slots · zone {timezone}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
          <Button onClick={() => void save()} disabled={!dirty || saving}>
            {saving ? <Loader2 className="animate-spin" /> : <Save />}
            Save
          </Button>
        </div>
      </header>

      {timezone !== browserTz && (
        <p className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-500">
          Your device uses zone {browserTz}, while this account uses {timezone}. Times on this page
          follow the account zone.
        </p>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Add a slot</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-end gap-2">
                <select
                  value={dayInput}
                  onChange={(e) => setDayInput(Number(e.target.value))}
                  className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  {DOW_LABELS.map((d, i) => (
                    <option key={i} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
                <Input
                  className="w-24"
                  value={timeInput}
                  onChange={(e) => setTimeInput(e.target.value)}
                  placeholder="09:00"
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    const minute = hhmmToMinute(timeInput)
                    if (minute === null) {
                      toast.error('Invalid time format (example: 09:00)')
                      return
                    }
                    addSlot(dayInput, minute)
                  }}
                >
                  <Plus />
                  Add
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <Button
                    key={p.label}
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setDraft(p.make())
                      setDirty(true)
                    }}
                  >
                    {p.label}
                  </Button>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDraft([])
                    setDirty(true)
                  }}
                >
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Generate slots automatically</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="mb-2 text-sm text-muted-foreground">Days</p>
                <div className="flex flex-wrap gap-1.5">
                  {DOW_LABELS.map((label, dow) => {
                    const aktif = genDays.includes(dow)
                    return (
                      <button
                        key={dow}
                        type="button"
                        onClick={() =>
                          setGenDays((d) =>
                            d.includes(dow) ? d.filter((x) => x !== dow) : [...d, dow].sort(),
                          )
                        }
                        className={cn(
                          'h-9 min-w-11 rounded-md border px-2.5 text-sm transition-colors',
                          aktif
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-input hover:bg-muted',
                        )}
                      >
                        {label}
                      </button>
                    )
                  })}
                  <Button variant="ghost" size="sm" onClick={() => setGenDays([0, 1, 2, 3, 4, 5, 6])}>
                    All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setGenDays([1, 2, 3, 4, 5])}>
                    Weekdays
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">From</label>
                  <Input
                    className="w-24"
                    value={genStart}
                    onChange={(e) => setGenStart(e.target.value)}
                    placeholder="08:00"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Until</label>
                  <Input
                    className="w-24"
                    value={genEnd}
                    onChange={(e) => setGenEnd(e.target.value)}
                    placeholder="20:00"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Slots per day</label>
                  <Input
                    className="w-24"
                    type="number"
                    min={1}
                    max={MAX_PER_DAY}
                    value={genPerDay}
                    onChange={(e) => setGenPerDay(e.target.value)}
                  />
                </div>
                <Button onClick={applyGenerate} disabled={genDays.length === 0}>
                  <Wand2 />
                  Generate
                </Button>
              </div>

              {genPreview && (
                <p className="text-sm text-muted-foreground">
                  {genPreview.total} slots on {genDays.length} days · example {DOW_LABELS[genDays[0]]}:{' '}
                  <span className="text-foreground">{genPreview.contoh.join(', ')}</span>
                  <br />
                  <span className="text-xs">
                    Times are randomized within the range, no two share the same minute, and each day
                    differs. Press again to reshuffle.
                  </span>
                </p>
              )}
              {genError && <p className="text-sm text-destructive">{genError}</p>}
              {genWarning && <p className="text-sm text-amber-500">{genWarning}</p>}

              <p className="text-xs text-muted-foreground">
                The selected days are <strong>replaced entirely</strong>; other days are left
                intact — so you can set weekdays and weekends separately.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-7 gap-2">
                {DOW_LABELS.map((label, dow) => (
                  <div key={dow} className="space-y-2">
                    <p className="text-center text-xs font-medium text-muted-foreground">{label}</p>
                    <div className="min-h-32 space-y-1.5 rounded-md border border-dashed p-1.5">
                      {(byDow.get(dow) ?? []).map((minute) => (
                        <button
                          key={minute}
                          onClick={() => removeSlot(dow, minute)}
                          className={cn(
                            'group flex w-full items-center justify-center gap-1 rounded bg-primary/15 px-1 py-1 text-xs font-medium text-primary',
                            'hover:bg-destructive/15 hover:text-destructive',
                          )}
                          title="Click to delete"
                        >
                          {minuteToHHMM(minute)}
                          <X className="hidden size-3 group-hover:block" />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Click a slot to delete it. Changes take effect after Save. Actual publish times
                are accurate to ±1–2 minutes (scheduler granularity).
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Next up</CardTitle>
          </CardHeader>
          <CardContent>
            {preview && preview.instants.length > 0 ? (
              <ol className="space-y-1.5 text-sm">
                {preview.instants.map((i) => (
                  <li key={i.at} className="tabular-nums text-muted-foreground">
                    {i.local}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-muted-foreground">
                No saved slots yet — save first to see the schedule.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
